import AddIcon from '@mui/icons-material/Add';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import AttachFile from '@mui/icons-material/AttachFile';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Close from '@mui/icons-material/Close';
import InsertDriveFile from '@mui/icons-material/InsertDriveFile';
import Send from '@mui/icons-material/Send';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ChatPanel from 'components/common/ChatPanel';
import Conversation from 'components/common/Conversation';
import type React from 'react';
import { type ClipboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import {
  useCreateDemochatClientMutation,
  useCreateDemochatMessageMutation,
  useCreateDemochatMessageWithFilesMutation,
  useGenerateChatDescriptionMutation,
  useGetDemoClientMessagesQuery,
} from 'services/api/demochat';
import type { Message } from 'services/types/message';
import {
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  SHADOW,
  SURFACE_CHAT,
  SURFACE_CONTAINER_LOWEST,
  SURFACE_TINT,
} from 'styles/tokens';
import { type Ably, getAblyChannel } from 'utils/ablyClient';
import { capitalizeFirstLetterOfEachWord } from 'utils/capitalize';
import type { ChatHistoryItem } from 'utils/chatStorage';
import {
  deleteChatFromHistory,
  getChatHistory,
  getClientPhoneIdentifier,
  saveChatToHistory,
  setClientPhoneIdentifier,
  updateChatDescription,
} from 'utils/chatStorage';
import getReCaptchaToken from 'utils/getReCaptchaToken';
import { subscribeChannel } from 'utils/pusherClient';

import ChatHistorySidebar from './ChatHistorySidebar';

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_CLIENT_KEY || '';

/** Load reCAPTCHA script on-demand and hide the badge */
const ensureRecaptchaLoaded = () => {
  if (document.getElementById('recaptcha-key')) return;
  const script = document.createElement('script');
  script.id = 'recaptcha-key';
  script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
  document.body.appendChild(script);
  // Hide the badge — Google allows this for v3 as long as ToS attribution exists
  const style = document.createElement('style');
  style.textContent = '.grecaptcha-badge { visibility: hidden !important; }';
  document.head.appendChild(style);
};

type NewMessageChunkData = {
  messageId: string;
  content: string;
  status: 'chunk' | 'finished';
  timestamp: string;
};

interface DemoChatPanelProps {
  companyIdentifier: string;
  /** When true: no sidebar, no ChatWrapper, no navigate, auto-create client */
  embedded?: boolean;
  /** Callback to navigate (used by standalone page). If absent, navigate is a no-op. */
  onNavigateNewChat?: () => void;
  onSelectChat?: (phoneIdentifier: string) => void;
  /** Initial search params for message/autoStartChat. Only used in standalone mode. */
  initialMessage?: string;
  autoStartChat?: boolean;
}

const DemoChatPanel: React.FC<DemoChatPanelProps> = ({
  companyIdentifier,
  embedded = false,
  onNavigateNewChat,
  onSelectChat,
  initialMessage,
  autoStartChat,
}) => {
  const [clientPhone, setClientPhone] = useState<string | null>(() => getClientPhoneIdentifier(companyIdentifier));
  const [messageToSend, setMessageToSend] = useState(initialMessage || '');
  const [createDemochatMessage] = useCreateDemochatMessageMutation();
  const [createDemochatClient] = useCreateDemochatClientMutation();
  const [conversation, setConversation] = useState<Message[]>([]);
  const conversationRef = useRef<Message[]>([]);
  const finishedMessagesIdsRef = useRef<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [filesToSend, setFilesToSend] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<string[]>([]);
  const [isTyping, setIsTyping] = useState(0);
  const [newMessageChunk, setNewMessageChunk] = useState('');
  const messageChunkIdsRef = useRef<string[]>([]);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isCreatingClient, setIsCreatingClient] = useState(false);
  const [isCreatingNewChat, setIsCreatingNewChat] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarHovered, setSidebarHovered] = useState(false);

  const [chatHistory, setChatHistory] = useState<ChatHistoryItem[]>(() => getChatHistory(companyIdentifier));
  const [generateDescription] = useGenerateChatDescriptionMutation();

  const { data: messages, refetch } = useGetDemoClientMessagesQuery(
    { clientPhoneIdentifier: clientPhone ?? '' },
    { skip: !clientPhone }
  );

  const [createDemochatMessageWithFiles, { isLoading: isSendingMessageWithFiles }] =
    useCreateDemochatMessageWithFilesMutation();

  // Auto-create client in embedded mode only on initial mount if none exists
  const didAutoCreate = useRef(false);
  useEffect(() => {
    if (embedded && !clientPhone && !isCreatingClient && !didAutoCreate.current && !isCreatingNewChat) {
      didAutoCreate.current = true;
      setIsCreatingClient(true);
      ensureRecaptchaLoaded();
      getReCaptchaToken()
        .then((recaptchaToken) => createDemochatClient({ companyIdentifier, recaptchaToken }).unwrap())
        .then((result: { id: string }) => {
          setClientPhoneIdentifier(companyIdentifier, result.id);
          setClientPhone(result.id);
        })
        .catch((err) => {
          console.error('Error creating demo client:', err);
        })
        .finally(() => setIsCreatingClient(false));
    }
  }, [embedded, clientPhone, companyIdentifier, isCreatingNewChat, isCreatingClient, createDemochatClient]);

  // If not embedded and no client, redirect to new chat
  useEffect(() => {
    if (!embedded && !clientPhone && onNavigateNewChat) {
      onNavigateNewChat();
    }
  }, [embedded, clientPhone, onNavigateNewChat]);

  useEffect(() => {
    if (clientPhone) {
      refetch().catch(() => {
        console.error('Error refetching messages');
      });
    }
  }, [clientPhone, refetch]);

  useEffect(() => {
    if (messages) {
      setConversation(messages);
      conversationRef.current = messages;
      setNewMessageChunk('');
      setIsTyping(0);

      if (clientPhone) {
        const messageCount = messages.length;
        if (messageCount > 0) {
          saveChatToHistory(companyIdentifier, clientPhone, undefined, messageCount);
          setChatHistory(getChatHistory(companyIdentifier));
        }
      }
    }
  }, [messages, clientPhone, companyIdentifier]);

  useEffect(() => {
    if (isTyping > 0) {
      setConversation((prev) => prev.map((message) => ({ ...message, status: 'read' })));

      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        setIsTyping(0);
        typingTimeoutRef.current = null;
      }, 25000);
      return;
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
  }, [isTyping]);

  useEffect(() => {
    const urls = filesToSend.map((file) => URL.createObjectURL(file));
    setFilePreviews(urls);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [filesToSend]);

  // Pusher + Ably subscriptions
  useEffect(() => {
    if (!clientPhone) return;

    const channelName = `chat-${clientPhone}`;
    const { channel, unsubscribe } = subscribeChannel(channelName);
    const newMessageHandler = () => {
      refetch().catch(() => {
        console.error('Error refetching messages');
      });
    };
    const typingHandler = () => setIsTyping((prev) => prev + 1);
    channel.bind('new-message', newMessageHandler);
    channel.bind('typing', typingHandler);

    const ablyChannel = getAblyChannel(`chat-${clientPhone}`);
    const ablyListener = (message: Ably.InboundMessage) => {
      const data = message.data as NewMessageChunkData;

      if (finishedMessagesIdsRef.current.has(data.messageId)) return;

      if (data.timestamp) {
        const lastAssistantMessage = conversationRef.current.filter((msg) => msg.role === 'assistant').at(-1);
        if (lastAssistantMessage?.createdAt && new Date(data.timestamp) < new Date(lastAssistantMessage.createdAt)) {
          return;
        }
      }

      if (data.status === 'finished') {
        finishedMessagesIdsRef.current.add(data.messageId);
        setNewMessageChunk(data.content);
        return;
      }

      if (messageChunkIdsRef.current.includes(data.messageId) && messageChunkIdsRef.current.at(-1) === data.messageId) {
        setNewMessageChunk((chunk) => (chunk.includes(data.content) ? chunk : data.content));
      } else if (!messageChunkIdsRef.current.includes(data.messageId)) {
        setNewMessageChunk(data.content);
        messageChunkIdsRef.current = [...messageChunkIdsRef.current, data.messageId];
      } else {
        return;
      }

      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      } else {
        setIsTyping((prev) => prev + 1);
      }
      typingTimeoutRef.current = setTimeout(() => {
        setIsTyping(0);
        typingTimeoutRef.current = null;
      }, 25000);
    };
    ablyChannel.subscribe('new-message-chunk', ablyListener).catch((err: Error) => {
      console.error('[Ably] Error subscribing:', err);
    });

    return () => {
      channel.unbind('new-message', newMessageHandler);
      channel.unbind('typing', typingHandler);
      unsubscribe();
      ablyChannel.unsubscribe('new-message-chunk', ablyListener);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
    };
  }, [clientPhone, refetch]);

  // Auto start chat on mount
  useEffect(() => {
    if (autoStartChat && clientPhone && initialMessage && initialMessage.length >= 3) {
      createDemochatMessage({ clientPhoneIdentifier: clientPhone, message: initialMessage })
        .unwrap()
        .then(() => {
          setMessageToSend((current) => (current === initialMessage ? '' : current));
          return refetch();
        })
        .catch((err) => console.error('Error sending initial message', err));
    }
  }, [initialMessage, clientPhone, createDemochatMessage, autoStartChat, refetch]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (!files || files.length === 0 || !clientPhone) return;

    const validFiles: File[] = [];
    const allowedMimeTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'];

    Array.from(files).forEach((file) => {
      if (!allowedMimeTypes.includes(file.type)) return;
      if (file.size > 5000000) return;
      validFiles.push(file);
    });

    if (validFiles.length > 0) setFilesToSend((prev) => [...prev, ...validFiles]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSendFilesWithMessage = async () => {
    if (!clientPhone || filesToSend.length === 0) return;
    const message = messageToSend.trim();
    const files = [...filesToSend];
    setFilesToSend([]);
    setMessageToSend('');

    const optimisticMessages: Message[] = files.map((file) => ({
      id: Math.random(),
      createdAt: new Date(),
      updatedAt: new Date(),
      status: 'sent to api',
      text: '',
      fileName: file.name,
      sender: 'client',
      role: 'user',
    })) as Message[];

    if (message) {
      optimisticMessages.push({
        id: Math.random(),
        createdAt: new Date(),
        updatedAt: new Date(),
        status: 'sent to api',
        text: message,
        sender: 'client',
        role: 'user',
      } as Message);
    }

    setConversation((prev) => [...prev, ...optimisticMessages]);

    try {
      await createDemochatMessageWithFiles({
        clientPhoneIdentifier: clientPhone,
        files,
        message: message || undefined,
      }).unwrap();
      refetch().catch(() => {
        console.error('Error refetching messages');
      });
    } catch (err) {
      console.error('Error uploading files:', err);
    }
  };

  const handleRemoveFile = (index: number) => setFilesToSend((prev) => prev.filter((_, i) => i !== index));

  const handleSendMessage = (e?: React.KeyboardEvent<HTMLDivElement>) => {
    if (e) e.preventDefault();

    if (filesToSend.length > 0) {
      handleSendFilesWithMessage().catch((err) => console.error('Error sending files:', err));
      return;
    }

    if (!clientPhone || !messageToSend || messageToSend.length === 0) return;
    const message = messageToSend.trim();
    const optimisticId = -Date.now();
    setMessageToSend('');
    setConversation((prev) => [
      ...prev,
      {
        id: optimisticId,
        createdAt: new Date(),
        updatedAt: new Date(),
        status: 'sent to api',
        text: message,
        sender: 'client',
        role: 'user',
      } as Message,
    ]);
    createDemochatMessage({ clientPhoneIdentifier: clientPhone, message })
      .unwrap()
      .then(() => refetch())
      .catch((err) => {
        console.error('Error sending message', err);
        setConversation((prev) => prev.filter((m) => m.id !== optimisticId));
      });
  };

  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    const items = e.clipboardData?.items;
    if (!items || items.length === 0) return;
    const allowedMimeTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'];
    const pastedFiles: File[] = [];
    Array.from(items).forEach((item) => {
      const file = item.getAsFile();
      if (file && allowedMimeTypes.includes(file.type) && file.size <= 5000000) pastedFiles.push(file);
    });
    if (pastedFiles.length > 0) {
      setFilesToSend((prev) => [...prev, ...pastedFiles]);
      e.preventDefault();
    }
  };

  const handleQuickReply = useCallback(
    (text: string) => {
      if (!clientPhone) return;
      const optimisticId = -Date.now();
      setConversation((prev) => [
        ...prev,
        {
          id: optimisticId,
          createdAt: new Date(),
          updatedAt: new Date(),
          status: 'sent to api',
          text,
          sender: 'client',
          role: 'user',
        } as Message,
      ]);
      createDemochatMessage({ clientPhoneIdentifier: clientPhone, message: text })
        .unwrap()
        .then(() => refetch())
        .catch((err) => {
          console.error('Error sending quick reply', err);
          setConversation((prev) => prev.filter((m) => m.id !== optimisticId));
        });
    },
    [clientPhone, createDemochatMessage, refetch]
  );

  const handleNewConversation = () => {
    setIsCreatingNewChat(true);
    ensureRecaptchaLoaded();
    // Clear current state immediately so old messages don't flash back
    setClientPhone(null);
    setConversation([]);
    conversationRef.current = [];
    finishedMessagesIdsRef.current = new Set();
    messageChunkIdsRef.current = [];
    setNewMessageChunk('');
    setIsTyping(0);

    getReCaptchaToken()
      .then((recaptchaToken) => createDemochatClient({ companyIdentifier, recaptchaToken }).unwrap())
      .then((result: { id: string }) => {
        setClientPhoneIdentifier(companyIdentifier, result.id);
        setClientPhone(result.id);
      })
      .catch((err) => {
        console.error('Error creating new conversation:', err);
      })
      .finally(() => setIsCreatingNewChat(false));
  };

  const handleSelectChat = (phoneIdentifier: string) => {
    setClientPhoneIdentifier(companyIdentifier, phoneIdentifier);
    if (onSelectChat) {
      onSelectChat(phoneIdentifier);
    } else if (embedded) {
      // In embedded mode, switch chat in-place without reload
      setClientPhone(phoneIdentifier);
      setConversation([]);
      conversationRef.current = [];
      finishedMessagesIdsRef.current = new Set();
      messageChunkIdsRef.current = [];
      setNewMessageChunk('');
      setIsTyping(0);
    } else {
      window.location.reload();
    }
  };

  const handleDeleteChat = (phoneIdentifier: string) => {
    deleteChatFromHistory(companyIdentifier, phoneIdentifier);
    setChatHistory(getChatHistory(companyIdentifier));
    if (phoneIdentifier === clientPhone && onNavigateNewChat) onNavigateNewChat();
  };

  // Generate descriptions for old chats
  useEffect(() => {
    const history = getChatHistory(companyIdentifier);
    history.forEach((chat) => {
      if (!chat.description && chat.lastMessageCount && clientPhone !== chat.phoneIdentifier) {
        generateDescription({ clientPhoneIdentifier: chat.phoneIdentifier })
          .unwrap()
          .then((response: { description: string }) => {
            if (response.description) {
              updateChatDescription(companyIdentifier, chat.phoneIdentifier, response.description);
              setChatHistory(getChatHistory(companyIdentifier));
            }
          })
          .catch(() => {});
      }
    });
  }, [companyIdentifier, clientPhone, generateDescription]);

  // Loading state for embedded mode
  if (embedded && !clientPhone) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2, opacity: 0.6 }}>
          Iniciando chat de prueba...
        </Typography>
      </Box>
    );
  }

  // ── Chat content (shared between standalone and embedded) ──
  const chatContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <Conversation
          conversation={conversation || []}
          isFilteredConversation
          isClientView
          isAssistantWriting={isTyping > 0}
          newMessageChunk={newMessageChunk}
          fullHeight
          onQuickReplyClick={handleQuickReply}
        />
      </Box>
      <Box sx={{ width: '100%', p: 1, pt: 0.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* File Previews */}
        {filesToSend.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: 1, backgroundColor: '#fff', borderRadius: '12px' }}>
            {filesToSend.map((file, index) => (
              <Box
                key={`${file.name}-${file.size}-${file.lastModified}`}
                sx={{
                  position: 'relative',
                  maxWidth: 300,
                  height: 200,
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '1px solid #e0e0e0',
                  backgroundColor: '#f5f5f5',
                }}
              >
                {file.type.startsWith('image/') ? (
                  <img
                    src={filePreviews[index]}
                    alt={file.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 0.5,
                    }}
                  >
                    <InsertDriveFile sx={{ fontSize: 32, color: '#d32f2f' }} />
                    <Tooltip title={file.name}>
                      <Box
                        component="span"
                        sx={{
                          fontSize: '10px',
                          textAlign: 'center',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          width: '100%',
                          color: '#666',
                        }}
                      >
                        PDF
                      </Box>
                    </Tooltip>
                  </Box>
                )}
                <IconButton
                  size="small"
                  onClick={() => handleRemoveFile(index)}
                  sx={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    color: '#fff',
                    width: 20,
                    height: 20,
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' },
                  }}
                >
                  <Close sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        {/* Input Area */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept="image/*,.pdf"
            multiple
            onChange={handleFileUpload}
          />
          <TextField
            placeholder="Mensaje"
            fullWidth
            multiline
            maxRows={10}
            onPaste={handlePaste}
            sx={{
              backgroundColor: '#fff',
              borderRadius: '24px',
              '& .MuiOutlinedInput-root': { borderRadius: '24px', padding: '12px' },
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              '& .MuiInputBase-input': { paddingLeft: '8px', caretColor: '#888888' },
            }}
            value={messageToSend}
            onChange={(e) => setMessageToSend(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage(e)}
            autoFocus={!embedded}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      tabIndex={-1}
                      onClick={() => fileInputRef.current?.click()}
                      sx={{ color: '#54656f' }}
                    >
                      <AttachFile />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
          <IconButton
            sx={{
              backgroundColor: messageToSend.length > 0 || filesToSend.length > 0 ? '#1daa61' : SURFACE_TINT,
              color: '#fff',
              width: '48px',
              height: '48px',
              minWidth: '48px',
              minHeight: '48px',
              '&:hover': {
                backgroundColor: messageToSend.length > 0 || filesToSend.length > 0 ? '#178a4e' : '#999999',
              },
              '&:disabled': { backgroundColor: '#b3b3b3', color: '#fff' },
              transition: 'all 0.2s ease',
            }}
            onClick={() => handleSendMessage()}
            disabled={messageToSend.length === 0 && filesToSend.length === 0}
          >
            {isSendingMessageWithFiles ? (
              <CircularProgress size={24} sx={{ color: 'white' }} />
            ) : (
              <Send sx={{ fontSize: '24px' }} />
            )}
          </IconButton>
        </Box>
      </Box>
    </Box>
  );

  // ── Embedded mode: white box with header + chat ──
  if (embedded) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: SURFACE_CONTAINER_LOWEST,
          borderRadius: RADIUS_CARD,
          boxShadow: SHADOW,
          overflow: 'hidden',
        }}
      >
        {/* Header with buttons */}
        <Box
          sx={{
            px: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: 52,
            flexShrink: 0,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: NAVY, fontFamily: 'Jost, sans-serif', marginLeft: '1rem' }}
          >
            Chat de Prueba
          </Typography>
          <Box sx={{ display: { xs: 'flex', md: sidebarCollapsed ? 'flex' : 'none' }, gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleNewConversation}
              disabled={isCreatingNewChat || isCreatingClient}
              sx={{
                bgcolor: NAVY,
                color: '#fff',
                fontFamily: 'Jost, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: RADIUS_COIN,
                '&:hover': { bgcolor: NAVY, opacity: 0.9 },
              }}
            >
              {isCreatingNewChat ? 'Creando...' : 'NUEVO CHAT'}
            </Button>
          </Box>
        </Box>

        {/* Sidebar + Chat area */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'row',
            mx: 2,
            mb: 2,
            minHeight: 0,
            overflow: 'visible',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              display: sidebarCollapsed ? 'none' : { xs: 'none', md: 'block' },
              '& > *': { height: '100% !important', width: '260px !important', borderRight: 'none !important' },
              height: '100%',
              flexShrink: 0,
            }}
            onMouseEnter={() => setSidebarHovered(true)}
            onMouseLeave={() => setSidebarHovered(false)}
          >
            <ChatHistorySidebar
              companyIdentifier={companyIdentifier}
              currentPhoneIdentifier={clientPhone}
              chatHistory={chatHistory}
              onSelectChat={handleSelectChat}
              onDeleteChat={handleDeleteChat}
              onNewChat={handleNewConversation}
            />
          </Box>
          {/* Desktop collapse toggle */}
          <IconButton
            size="small"
            onClick={() => setSidebarCollapsed((c) => !c)}
            onMouseEnter={() => setSidebarHovered(true)}
            onMouseLeave={() => setSidebarHovered(false)}
            sx={{
              display: { xs: 'none', md: 'flex' },
              position: 'absolute',
              top: 24,
              left: sidebarCollapsed ? -12 : 260 - 12,
              transition: 'left 0.3s ease, opacity 0.2s ease',
              zIndex: 10,
              width: 24,
              height: 24,
              opacity: sidebarCollapsed || sidebarHovered ? 1 : 0,
              color: `rgba(21, 24, 58, 0.5)`,
              backgroundColor: '#fff',
              boxShadow: SHADOW,
              '&:hover': { backgroundColor: '#fff', color: NAVY },
            }}
          >
            {sidebarCollapsed ? <ChevronRightIcon sx={{ fontSize: 18 }} /> : <ChevronLeftIcon sx={{ fontSize: 18 }} />}
          </IconButton>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: SURFACE_CHAT,
              borderRadius: RADIUS_CARD,
              overflow: 'hidden',
            }}
          >
            {chatContent}
          </Box>
        </Box>
      </Box>
    );
  }

  // ── Standalone mode: full layout with ChatWrapper + optional sidebar ──
  return (
    <Box sx={{ height: '100dvh', display: 'flex', flexDirection: 'row', position: 'relative' }}>
      <Box
        sx={{ display: sidebarCollapsed ? 'none' : { xs: 'none', md: 'block' } }}
        onMouseEnter={() => setSidebarHovered(true)}
        onMouseLeave={() => setSidebarHovered(false)}
      >
        <ChatHistorySidebar
          companyIdentifier={companyIdentifier}
          currentPhoneIdentifier={clientPhone}
          chatHistory={chatHistory}
          onSelectChat={handleSelectChat}
          onDeleteChat={handleDeleteChat}
        />
      </Box>
      {/* Desktop collapse toggle */}
      <IconButton
        size="small"
        onClick={() => setSidebarCollapsed((c) => !c)}
        onMouseEnter={() => setSidebarHovered(true)}
        onMouseLeave={() => setSidebarHovered(false)}
        sx={{
          display: { xs: 'none', md: 'flex' },
          position: 'absolute',
          top: 92,
          left: sidebarCollapsed ? 12 : 400 - 12,
          transition: 'left 0.3s ease, opacity 0.2s ease',
          zIndex: 10,
          width: 24,
          height: 24,
          opacity: sidebarCollapsed || sidebarHovered ? 1 : 0,
          color: `rgba(21, 24, 58, 0.5)`,
          backgroundColor: '#fff',
          boxShadow: SHADOW,
          '&:hover': { backgroundColor: '#fff', color: NAVY },
        }}
      >
        {sidebarCollapsed ? <ChevronRightIcon sx={{ fontSize: 18 }} /> : <ChevronLeftIcon sx={{ fontSize: 18 }} />}
      </IconButton>
      <Box sx={{ flex: 1 }}>
        <ChatPanel>
          <ChatPanel.Header
            title={capitalizeFirstLetterOfEachWord(companyIdentifier || 'Demo Chat')}
            subtitle="En línea"
            rightAction={
              <Box sx={{ display: { xs: 'flex', md: sidebarCollapsed ? 'flex' : 'none' } }}>
                <IconButton onClick={handleNewConversation} sx={{ color: NAVY }}>
                  <AddCommentOutlinedIcon />
                </IconButton>
              </Box>
            }
          />
          <ChatPanel.Body>{chatContent}</ChatPanel.Body>
        </ChatPanel>
      </Box>
    </Box>
  );
};

export default DemoChatPanel;
